import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { FETCH_STATUSES, REWARDS_LEVEL } from 'resources'

import actions from './actions'

import { SEND_SMS_STATUSES, PHONE_VERIFY_STATUS, PROFILE_PROPERTIES } from './constants'

const mapResponseToProfileInfoData = (payload) =>
  Object.values(PROFILE_PROPERTIES).reduce((acc, { valueFieldKey, sourceFieldKey, verifiedFieldKey }) => {
    try {
      const value = payload[valueFieldKey]
      const source = payload[sourceFieldKey]
      const verified = payload[verifiedFieldKey]
      const dataValue = {
        value,
        source,
        verified,
      }
      return { ...acc, [valueFieldKey]: dataValue }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err)
      return acc
    }
  }, {})

export const initialState = {
  status: 'empty',
  balance: '0',
  phoneVerifyStatus: 'empty',
  sendingSMSCodeStatus: 'empty',
  creationDate: null,
  sellProfileData: true,
  referralLink: '',
  isReferredUser: false,
  isUnveilOnboarded: true,
  createAccountType: null,
  isKidProfilesChecked: false,
  kidProfilesLastCheckedDatetime: null,
  hasMatchingKidProfiles: false,
  hasPendingUnveilNotification: false,
  isUnveilPrompted: false,
  isLockedByAdmin: false,
  jackpotAutoPledgeAmount: null,
  autoRedeemSharesEnabled: false,
  isFinancialDividendMember: false,
  isFetchedAtLeastOnce: false,
  isBrowserDividendMember: false,
  isDeviceRewardsMember: false,
  completedRequiredProfileRewardsQuestions: false,
  isProfileRewardsMember: false,
  profileRewardsShareDatetime: null,
  hasMinAccountBalance: false,
  emailAddresses: null,
  phoneNumbers: null,
  totalEarnings: {
    financialEarnings: '0',
    audienceEarnings: '0',
    browsingEarnings: '0',
    deviceEarnings: '0',
  },
  userCode: '',
  isVerifiedUser: false,
  isVipReferrer: false,
  data: {},
  enablePushNotifications: false,
  enableEmailNotifications: false,
  enableSmsNotifications: false,
  hasActiveShoppingRewardsAccount: false,
  shoppingRewardsLatestEventDatetime: null,
  browserRewardsLatestEventDatetime: null,
  pendingNotificationModalType: null,
  hasPendingTransactions: false,
  pendingTransactionsAmount: '0',

  // Social Share Data
  branchRedemptionLink: '',

  // Referral Data
  referralDataStatus: FETCH_STATUSES.NOOP,
  referralBonusTotalAmount: '0',
  referralRewardsTotalAmount: '0',
  referralRefereeCount: 0,
  referralRefereePaycheckTierCounts: {
    [REWARDS_LEVEL.ZERO]: 0,
    [REWARDS_LEVEL.ONE]: 0,
    [REWARDS_LEVEL.TWO]: 0,
    [REWARDS_LEVEL.THREE]: 0,
    [REWARDS_LEVEL.FOUR]: 0,
  },

  // Device Rewards Data
  ipAddress: null,
  deviceRewardsDeviceDataJson: null,
  deviceRewardsAppsDataJson: null,
  deviceUserAgent: null,
  deviceLanguageCode: null,
  browsingRewardsStatistics: null,

  // Subscription
  subscriptionType: null,
  subscriptionBillingCycle: null,
  subscriptionExpiryDatetime: null,
  isSubscriptionRenewalEnabled: false,

  // Personalization
  userHandle: null,
  profilePictureUrl: null,
}

const reducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case actions.fetchProfileInfo.INIT_TYPE: {
      return {
        ...state,
        status: 'fetching',
      }
    }
    case actions.updateAccountField.SUCCESS_TYPE:
    case actions.fetchProfileInfo.SUCCESS_TYPE: {
      return {
        ...state,
        status: 'success',
        balance: payload.balance,
        plUid: payload.plUid,
        id: payload.id,
        userAlias: payload.userAlias,
        creationDate: payload.created,
        referralLink: payload.referralLink,
        branchRedemptionLink: payload.branchRedemptionLink,
        sellProfileData: payload.sellProfileData,
        isUnveilOnboarded: payload.isUnveilOnboarded,
        createAccountType: payload.createAccountType,
        isKidProfilesChecked: payload.isKidProfilesChecked,
        kidProfilesLastCheckedDatetime: payload.kidProfilesLastCheckedDatetime,
        hasMatchingKidProfiles: payload.hasMatchingKidProfiles,
        hasPendingUnveilNotification: payload.hasPendingUnveilNotification,
        isUnveilPrompted: payload.isUnveilPrompted,
        isLockedByAdmin: payload.isLockedByAdmin,
        jackpotAutoPledgeAmount: payload.jackpotAutoPledgeAmount,
        autoRedeemSharesEnabled: payload.autoRedeemSharesEnabled,
        isReferredUser: payload.isReferredUser,
        isFinancialDividendMember: payload.isFinancialDividendMember,
        isFetchedAtLeastOnce: true,
        isBrowserDividendMember: payload.isBrowserRewardsMember,
        isDeviceRewardsMember: payload.isDeviceRewardsMember,
        completedRequiredProfileRewardsQuestions: payload.completedRequiredProfileRewardsQuestions,
        isProfileRewardsMember: payload.isProfileRewardsMember,
        hasActiveShoppingRewardsAccount: payload.hasActiveShoppingRewardsAccount,
        shoppingRewardsLatestEventDatetime: payload.shoppingRewardsLatestEventDatetime,
        browserRewardsLatestEventDatetime: payload.browserRewardsLatestEventDatetime,
        profileRewardsShareDatetime: payload.profileRewardsShareDatetime,
        hasMinAccountBalance: payload.hasMinimumBalance,
        enablePushNotifications: payload.enableNotifications,
        enableEmailNotifications: payload.enableEmailNotifications,
        enableSmsNotifications: payload.enableSmsNotifications,
        totalEarnings: {
          financialEarnings: payload.financialEarnings,
          audienceEarnings: payload.audienceEarnings,
          browsingEarnings: payload.browsingEarnings,
          deviceEarnings: payload.deviceEarnings,
        },
        userCode: payload.userCode,
        isVerifiedUser: payload.isVerifiedUser,
        isVipReferrer: payload.isVipReferrer,
        pendingNotificationModalType: payload.pendingNotificationModalType,
        ipAddress: payload.ipAddress,
        deviceRewardsDeviceDataJson: payload.deviceRewardsDeviceDataJson,
        deviceRewardsAppsDataJson: payload.deviceRewardsAppsDataJson,
        data: mapResponseToProfileInfoData(payload),
        deviceUserAgent: payload.deviceUserAgent,
        deviceLanguageCode: payload.deviceLanguageCode,
        emailAddresses: payload.emailAddresses,
        phoneNumbers: payload.phoneNumbers,
        subscriptionType: payload.subscriptionType,
        subscriptionBillingCycle: payload.subscriptionBillingCycle,
        subscriptionExpiryDatetime: payload.subscriptionExpiryDatetime,
        isSubscriptionRenewalEnabled: payload.isSubscriptionRenewalEnabled,
        userHandle: payload.userHandle,
        profilePictureUrl: payload.profilePictureUrl,
        hasPendingTransactions: payload.hasPendingTransactions,
        pendingTransactionsAmount: payload.pendingTransactionsAmount,
      }
    }
    case actions.fetchReferralData.INIT_TYPE: {
      return {
        ...state,
        referralDataStatus: FETCH_STATUSES.FETCHING,
      }
    }
    case actions.fetchReferralData.SUCCESS_TYPE: {
      return {
        ...state,
        referralDataStatus: FETCH_STATUSES.SUCCESS,
        referralBonusTotalAmount: payload.referralBonusTotalAmount,
        referralRewardsTotalAmount: payload.referralRewardsTotalAmount,
        referralRefereeCount: payload.referralRefereeCount,
        referralRefereePaycheckTierCounts: {
          [REWARDS_LEVEL.ZERO]: payload.referralRefereePaycheckTier0Count,
          [REWARDS_LEVEL.ONE]: payload.referralRefereePaycheckTier1Count,
          [REWARDS_LEVEL.TWO]: payload.referralRefereePaycheckTier2Count,
          [REWARDS_LEVEL.THREE]: payload.referralRefereePaycheckTier3Count,
          [REWARDS_LEVEL.FOUR]: payload.referralRefereePaycheckTier4Count,
        },
      }
    }
    case actions.fetchReferralData.FAILURE_TYPE: {
      return {
        ...state,
        referralDataStatus: FETCH_STATUSES.FAILURE,
      }
    }
    case actions.setIsProfileRewardsMember.SUCCESS_TYPE: {
      const { isProfileRewardsMember, profileRewardsShareDatetime, pendingNotificationModalType } = payload
      return {
        ...state,
        isProfileRewardsMember,
        profileRewardsShareDatetime,
        pendingNotificationModalType,
      }
    }
    case actions.updateAccountField.INIT_TYPE: {
      return {
        ...state,
        status: 'updating',
      }
    }
    case actions.updateAccountField.FAILURE_TYPE: {
      return {
        ...state,
        status: 'failure',
      }
    }
    case actions.addPhoneNumberSendSmsCode.INIT_TYPE: {
      return {
        ...state,
        sendingSMSCodeStatus: SEND_SMS_STATUSES.sendSmsCodeRegistering,
      }
    }

    case actions.addPhoneNumberSendSmsCode.SUCCESS_TYPE: {
      return {
        ...state,
        sendingSMSCodeStatus: SEND_SMS_STATUSES.sendSmsCodeSuccess,
      }
    }
    case actions.addPhoneNumberSendSmsCode.FAILURE_TYPE: {
      return {
        ...state,
        sendingSMSCodeStatus: SEND_SMS_STATUSES.sendSmsCodeFailure,
      }
    }
    case actions.addPhoneNumberVerifySmsCode.INIT_TYPE: {
      return {
        ...state,
        phoneVerifyStatus: PHONE_VERIFY_STATUS.phoneStatusVerifying,
      }
    }
    case actions.addPhoneNumberVerifySmsCode.SUCCESS_TYPE: {
      return {
        ...state,
        phoneVerifyStatus: PHONE_VERIFY_STATUS.phoneStatusSuccess,
      }
    }
    case actions.addPhoneNumberVerifySmsCode.FAILURE_TYPE: {
      return {
        ...state,
        phoneVerifyStatus: PHONE_VERIFY_STATUS.phoneStatusFailure,
      }
    }
    case actions.setSellProfileData.SUCCESS_TYPE: {
      return {
        ...state,
        sellProfileData: payload.sellProfileData,
      }
    }
    case actions.setIsFinancialDividendMember.SUCCESS_TYPE: {
      const { isFinancialDividendMember } = payload
      return {
        ...state,
        isFinancialDividendMember,
      }
    }
    case actions.setIsBrowsingRewardsMember.SUCCESS_TYPE: {
      const { isBrowserDividendMember } = payload
      return {
        ...state,
        isBrowserDividendMember,
      }
    }
    case actions.setIsDeviceRewardsMember.SUCCESS_TYPE: {
      const { isDeviceRewardsMember } = payload
      return {
        ...state,
        isDeviceRewardsMember,
      }
    }
    case actions.setIsUnveilPrompted.SUCCESS_TYPE: {
      const { isUnveilPrompted } = payload
      return {
        ...state,
        isUnveilPrompted,
      }
    }
    case actions.setEnableSmsNotifications.SUCCESS_TYPE: {
      const { enableSmsNotifications } = payload
      return {
        ...state,
        enableSmsNotifications,
      }
    }
    case actions.setEnableEmailNotifications.SUCCESS_TYPE: {
      const { enableEmailNotifications } = payload
      return {
        ...state,
        enableEmailNotifications,
      }
    }
    case actions.setHasPendingUnveilNotification.SUCCESS_TYPE: {
      const { hasPendingUnveilNotification } = payload
      return {
        ...state,
        hasPendingUnveilNotification,
      }
    }
    case actions.setPendingNotificationModalType.SUCCESS_TYPE: {
      const { pendingNotificationModalType } = payload
      return {
        ...state,
        pendingNotificationModalType,
      }
    }
    case actions.setDeviceData.SUCCESS_TYPE: {
      const { ipAddress, deviceLanguageCode, deviceUserAgent } = payload
      return {
        ...state,
        ipAddress,
        deviceLanguageCode,
        deviceUserAgent,
      }
    }
    case actions.fetchBrowsingRewardsStatistics.SUCCESS_TYPE: {
      const { records } = payload
      return {
        ...state,
        browsingRewardsStatistics: records,
      }
    }
    case actions.setIsUnveilOnboarded.SUCCESS_TYPE: {
      const { isUnveilOnboarded } = payload
      return {
        ...state,
        isUnveilOnboarded,
      }
    }
    case 'RESET': {
      return initialState
    }
    default:
      return state
  }
}

const persistConfig = {
  key: 'profileInfo',
  blacklist: ['isFetchedAtLeastOnce'],
  storage,
}

export default persistReducer(persistConfig, reducer)
